import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import {
  TRIF_BASES, MVIR_BASES, MONTH_NAMES,
  EXPOSURE_SCOPES, EXPOSURE_SCOPE_LABELS,
  type MetricSettings, type ExposureScope,
} from '../../lib/safety-metrics';

/**
 * Settings → Safety Metrics.
 *
 * Every field here moves a published number. Changing the TRIF base rescales
 * TRIF, LTIF, DART and Severity at once; changing the fiscal year start moves
 * the YTD window and therefore which incidents are inside it. That is why each
 * control says what it affects rather than just what it is.
 *
 * Rendered as the Metrics tab of the Safety workspace (SafetyWorkspace.tsx),
 * which owns the page title — so this component has no header of its own.
 */
export default function SafetyMetricsSettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['safety-metric-settings'],
    queryFn: () => api.safety.metricSettings().then((r) => unwrap<MetricSettings>(r.data)),
  });

  /* Held as strings so a half-typed threshold is not coerced to a number on
   * every keystroke. Seeded from the server rather than from a hardcoded
   * default — a local default would silently disagree with the row the API
   * creates on read, and the disagreement would only surface on Save. */
  const [form, setForm] = useState<{
    trifBase: string;
    mvirBase: string;
    mviCostThreshold: string;
    fiscalYearStartMonth: string;
    exposureScope: ExposureScope;
  } | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data) return;
    setForm({
      trifBase: String(data.trifBase),
      mvirBase: String(data.mvirBase),
      mviCostThreshold: String(data.mviCostThreshold),
      fiscalYearStartMonth: String(data.fiscalYearStartMonth),
      exposureScope: (data.exposureScope as ExposureScope) ?? 'all_employee',
    });
    setDirty(false);
  }, [data]);

  const set = <K extends keyof NonNullable<typeof form>>(key: K, value: NonNullable<typeof form>[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: any) => api.safety.updateMetricSettings(payload),
    onSuccess: () => {
      /* The rates and the settings are the same fact seen twice. Without this
       * the metrics page keeps showing figures computed on the old base until a
       * hard refresh, which reads as the save not having worked. */
      queryClient.invalidateQueries({ queryKey: ['safety-metric-settings'] });
      queryClient.invalidateQueries({ queryKey: ['safety-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['analytics', 'safety'] });
      setDirty(false);
      toast.success('Safety metric settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  if (isLoading || !form) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  const selectCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white';

  return (
    <div className="min-h-full">
      <div className="px-6 py-4 space-y-3">
        <Field
          label="TRIF base"
          desc="Hours the rate is expressed per. 200,000 is the OSHA/OH&S convention — roughly 100 full-time employees for a year. Applies to TRIF, LTIF, DART and Severity Rate."
        >
          <select value={form.trifBase} onChange={(e) => set('trifBase', e.target.value)} className={selectCls}>
            {TRIF_BASES.map((b) => (
              <option key={b} value={b}>{b.toLocaleString('en-CA')} hours</option>
            ))}
          </select>
        </Field>

        <Field
          label="MVIR base"
          desc="Kilometres the motor vehicle incident rate is expressed per."
        >
          <select value={form.mvirBase} onChange={(e) => set('mvirBase', e.target.value)} className={selectCls}>
            {MVIR_BASES.map((b) => (
              <option key={b} value={b}>{b.toLocaleString('en-CA')} km</option>
            ))}
          </select>
        </Field>

        <Field
          label="MVI cost threshold"
          desc="Repair cost above which a motor vehicle incident is treated as reportable. Recorded in dollars."
        >
          <input
            type="number" min="0" step="any" inputMode="decimal"
            value={form.mviCostThreshold}
            onChange={(e) => set('mviCostThreshold', e.target.value)}
            className={selectCls}
          />
        </Field>

        <Field
          label="Fiscal year start month"
          desc="Where the year-to-date window begins. Changing this moves which incidents fall inside YTD, so the figure will shift."
        >
          <select
            value={form.fiscalYearStartMonth}
            onChange={(e) => set('fiscalYearStartMonth', e.target.value)}
            className={selectCls}
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Exposure scope"
          desc="Who the monthly hours are meant to cover. This labels the figure — it does not filter it. The hours come from approved clock-in and tickets unless the office edits a month on the Exposure Entry tab."
        >
          <select
            value={form.exposureScope}
            onChange={(e) => set('exposureScope', e.target.value as ExposureScope)}
            className={selectCls}
          >
            {EXPOSURE_SCOPES.map((s) => (
              <option key={s} value={s}>{EXPOSURE_SCOPE_LABELS[s]}</option>
            ))}
          </select>
        </Field>

        <button
          onClick={() => saveMutation.mutate({
            trifBase: Number(form.trifBase),
            mvirBase: Number(form.mvirBase),
            mviCostThreshold: Number(form.mviCostThreshold),
            fiscalYearStartMonth: Number(form.fiscalYearStartMonth),
            exposureScope: form.exposureScope,
          })}
          disabled={!dirty || saveMutation.isPending}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors mt-4 ${
            dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, desc, children }: { label: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-fieldiq-muted mt-0.5">{desc}</p>
      </div>
      {children}
    </div>
  );
}
